import React, { JSX, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import LzXBaseField from '../BaseField';
import { lzFIsArray } from '@lz/services/Type';
import useLzFApiCall from '@lz/services/Api';
import LzMOption from '@lz/models/commons/Option';
import { LzTOpenObject } from '@lz/typings/types';

import './SelectField.scss';

export interface LzPSelectField {
  fluid?: boolean;
  required?: boolean;
  placeholder?: string;
  label?: string;
  name?: string;
  icon?: JSX.Element;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  size?: 'tiny' | 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  value?: string | number;
  options?: LzMOption[];
  refTableName?: string;
  refQueryFilters?: LzTOpenObject;
  refTableIdField?: string;
  refTableTitleField?: string;
  refFilterFieldKeys?: string[];
  modelToFilter?: LzTOpenObject;
  maxLimit?: number;
  noFirstOption?: boolean;
  firstOptionValue?: string;
  firstOptionText?: string;
  onChange?: (
    name: string,
    value: string | number,
    option?: LzMOption | null
  ) => void;
}

const LzXSelectField = (props: LzPSelectField) => {
  if (typeof props.value === 'undefined') {
    return <CircularProgress />;
  }

  const loadingFromDBAttempted = React.useRef(false);

  const [options, setOptions] = useState<LzMOption[] | null>(
    typeof props.options === 'undefined' ? null : props.options
  );
  const [filteredOptions, setFilteredOptions] = useState<LzMOption[] | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const lzApiCall = useLzFApiCall();

  const loadOptions = async () => {
    if (props.refTableName && props.refTableName != '') {
      setLoading(true);
      setErrorMessage('');

      let params = {
        max: props.maxLimit || 0,
        ...(props.refQueryFilters || {}),
      };

      const data = await lzApiCall(
        'options/' + props.refTableName.replace(/_/g, '-'),
        params
      );

      // Load data if call was successful
      if (data) {
        setOptions(
          LzMOption.parseArray(
            data,
            props.refTableIdField || 'id',
            props.refTableTitleField || 'name',
            props.refFilterFieldKeys
          )
        );
      } else {
        setErrorMessage('Cannot load options, please view logs...');
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (typeof props.options === 'undefined') {
      setOptions(null);
      if (!loadingFromDBAttempted.current) {
        loadOptions();
        loadingFromDBAttempted.current = true;
      }
    } else {
      setOptions(props.options);
    }
  }, [props.options]);

  // useLzEffectAfterMount() does not work if the select field is in some content that is toggled for display (like Dialog)
  useEffect(() => {
    // If options not set then filtered options are null as well
    if (!options) {
      setFilteredOptions(null);
      return;
    }

    // Build filtered options
    const temp: LzMOption[] = [];
    for (const option of options) {
      if (
        props.refFilterFieldKeys &&
        props.refFilterFieldKeys.length > 0 &&
        props.modelToFilter
      ) {
        let anyFailed = false;
        for (const filterFieldKey of props.refFilterFieldKeys) {
          if (
            typeof props.modelToFilter[filterFieldKey] !== 'undefined' &&
            typeof option[filterFieldKey] !== 'undefined' &&
            props.modelToFilter[filterFieldKey] !== option[filterFieldKey]
          ) {
            anyFailed = true;
          }
        }
        if (!anyFailed) {
          temp.push(option);
        }
      } else {
        temp.push(option);
      }
    }
    setFilteredOptions(temp);
  }, [props.refFilterFieldKeys, props.modelToFilter, options]);

  const isReady = filteredOptions && lzFIsArray(filteredOptions);

  const theName = props.name || '';
  const theId = props.id || 'id_' + theName;

  const handleOnChange = (val: string | number): void => {
    if (props.onChange) {
      props.onChange(
        theName,
        val,
        options ? options.filter((opt) => opt.value === val)[0] : null
      );
    }
  };

  return (
    <LzXBaseField
      id={theId}
      fluid={props.fluid}
      required={props.required}
      icon={props.icon}
      name={props.name}
      label={props.label}
      size={'tiny' === props.size ? 'small' : props.size || 'small'}
      variant={props.variant || 'standard'}
      className='lz-select-basefield'
    >
      {!isReady && (
        <div className='lz-select'>
          {loading && <CircularProgress />}
          {errorMessage !== '' && (
            <Alert variant='filled' color='warning'>
              {errorMessage}
            </Alert>
          )}
        </div>
      )}

      {isReady && (
        <Select
          required={props.required}
          fullWidth={props.fluid}
          name={props.name || ''}
          className={
            'lz-select' +
            (props.className ? ' ' + props.className : '') +
            ('tiny' === props.size ? ' lz-select-tiny' : '')
          }
          style={props.style || {}}
          size={'tiny' === props.size ? 'small' : props.size || 'small'}
          variant={props.variant || 'standard'}
          label={props.label}
          value={props.value}
          onChange={(evt) => handleOnChange(evt.target.value)}
        >
          {!props.noFirstOption && (
            <MenuItem
              value={
                typeof props.firstOptionValue === 'undefined'
                  ? 0
                  : props.firstOptionValue
              }
            >
              {props.firstOptionText || '- select any -'}
            </MenuItem>
          )}
          {filteredOptions.map((option, dex) => (
            <MenuItem key={'opt-' + dex} value={option.value}>
              {option.text}
            </MenuItem>
          ))}
        </Select>
      )}
    </LzXBaseField>
  );
};

export default LzXSelectField;
